/**
 * Request-a-demo engine for the HART page. Posts to /api/demo-requests and
 * upgrades/creates the lead with the product of interest.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CalendarClock, CheckCircle2, Phone, Mail } from "lucide-react";
import { API } from "@/App";

const ROLES = ["VP Supply Chain", "CFO / Director of Finance", "Rebate / Sourcing Analyst",
  "Materials Management", "IT Leader", "Other"];
const PRODUCTS = ["H.E.A.R.T Rebate Tracking", "IMS Bill-Only Implants", "Compliance Tracking",
  "Request Approval Workflow", "Courier Service Management", "Full six-product suite"];

const BLANK = { name: "", hospital_name: "", title: ROLES[0], email: "", phone: "",
  product_interest: PRODUCTS[0] };

const field = `w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-[15px]
  text-[#0b2545] placeholder:text-slate-400 outline-none transition-colors
  focus:border-sky-500 focus:ring-2 focus:ring-cyan-400/20`;

export const HartDemoForm = ({ roiSnapshot = null }) => {
  const [form, setForm] = useState(BLANK);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Tell us who you are");
    if (!form.email.includes("@")) return toast.error("A work email, please");
    if (!form.hospital_name.trim()) return toast.error("Which health system?");
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        ...form, source: "website", roi_snapshot: roiSnapshot || undefined,
      });
      setDone(true);
      toast.success("Request received", {
        description: "We'll come back within one business day with a sandbox and an ROI pass.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send that — call (281) 456-3458");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl border border-emerald-300 bg-emerald-50 p-8 text-center"
        data-testid="demo-form-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
        <h3 className="text-2xl font-extrabold text-[#0b2545] tracking-tight mb-2">
          You&apos;re on the calendar queue
        </h3>
        <p className="text-slate-600 max-w-md mx-auto">
          We&apos;ll reach out within one business day with a live sandbox and a first pass at the
          rebate dollars sitting in your own agreements.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
          <a href="tel:2814563458" className="inline-flex items-center gap-2 text-sky-700 hover:text-sky-800">
            <Phone className="w-4 h-4" /> (281) 456-3458
          </a>
          <a href="mailto:info@sterilemate.com" className="inline-flex items-center gap-2 text-sky-700 hover:text-sky-800">
            <Mail className="w-4 h-4" /> info@sterilemate.com
          </a>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit}
      className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-[0_2px_4px_rgba(11,37,69,0.05),0_24px_60px_-30px_rgba(11,37,69,0.4)]"
      data-testid="demo-request-form">
      <p className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-700 mb-2">
        Request a demo
      </p>
      <h3 className="text-2xl md:text-3xl font-black text-[#0b2545] tracking-tight mb-1">
        See it run against your own agreements
      </h3>
      <p className="text-sm text-slate-600 mb-6">
        Thirty minutes, your contracts, your tiers. You&apos;ll see the near-misses before we
        talk price.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Full name *" value={form.name}
          onChange={(e) => set("name", e.target.value)} data-testid="demo-field-name" />
        <input className={field} placeholder="Health system / hospital *" value={form.hospital_name}
          onChange={(e) => set("hospital_name", e.target.value)} data-testid="demo-field-system" />
        <select className={field} value={form.title}
          onChange={(e) => set("title", e.target.value)} data-testid="demo-field-role">
          {ROLES.map((r) => <option key={r} value={r} className="bg-white">{r}</option>)}
        </select>
        <select className={field} value={form.product_interest}
          onChange={(e) => set("product_interest", e.target.value)} data-testid="demo-field-product">
          {PRODUCTS.map((p) => <option key={p} value={p} className="bg-white">{p}</option>)}
        </select>
        <input className={field} type="email" placeholder="Work email *" value={form.email}
          onChange={(e) => set("email", e.target.value)} data-testid="demo-field-email" />
        <input className={field} placeholder="Phone" value={form.phone}
          onChange={(e) => set("phone", e.target.value)} data-testid="demo-field-phone" />
      </div>

      <button type="submit" disabled={sending}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl
          bg-gradient-to-r from-[#0b2545] to-[#13395f] px-6 py-4 text-[15px] font-black uppercase
          tracking-wider text-white transition-all hover:brightness-110
          disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
        data-testid="demo-form-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
        Book the H.E.A.R.T walkthrough
      </button>
      <p className="mt-3 text-[11px] text-slate-500 text-center">
        No ERP access needed for the demo. We&apos;ll run it on sample data unless you want yours loaded.
      </p>
    </form>
  );
};

export default HartDemoForm;
